import React, { useEffect, useState } from 'react';

import { Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useField } from 'formik';
import { Autocomplete, AutocompleteProps } from 'formik-mui';
import { useSnackbar } from 'notistack';
import { useDebounce } from 'use-debounce';

import { CissTextField } from 'components/fields/';
import { RequestListagemProps, RequestOptionsType, useRequestListagem } from 'hooks/useRequestListagem';

interface deviceOptionsProps {
    idTipoDispositivo: number;
    dsTipoDispositivo: string;
}

interface DeviceFieldProps extends AutocompleteProps<deviceOptionsProps, boolean | undefined, boolean | undefined, boolean | undefined> {
    label: string;
}

interface RequestListagemType extends RequestListagemProps {
    data: {
        data: deviceOptionsProps[];
    };
}

export function DeviceFieldFormik({ onChange, multiple = true, label = 'Dispositivos', ...PropsFormik }: DeviceFieldProps) {
    const [open, setOpen] = useState<boolean>(false);
    const [inputValue, setInputValue] = useState<string>('');
    const [query, setQuery] = useState('');
    const { enqueueSnackbar } = useSnackbar();
    const RequestListagem = useRequestListagem();
    const [field, { error }] = useField(PropsFormik.field.name);
    const [queryValue] = useDebounce(query, 500);

    const requestOptions: RequestOptionsType = {
        url: '/gestao/tipodispositivo',
        query: queryValue,
        sort: [{ property: 'idTipoDispositivo', direction: 'ASC' }],
        columns: 'idTipoDispositivo,dsTipoDispositivo',
        limit: 50,
    };

    const { isLoading, fetchStatus, data } = useQuery(
        [requestOptions],
        () => {
            const request: Promise<RequestListagemType> = RequestListagem(requestOptions);
            return request;
        },
        {
            keepPreviousData: false,
            enabled: open,
            onError: () => {
                enqueueSnackbar('Falha ao processar solicitação de dispositivos', { variant: 'error' });
            },
        },
    );

    useEffect(() => {
        if (open) {
            setQuery(inputValue);
        }
    }, [inputValue, open]);

    return (
        <Autocomplete
            {...PropsFormik}
            multiple={multiple}
            onChange={onChange}
            loading={isLoading && fetchStatus === 'fetching'}
            options={data?.data?.data ?? []}
            isOptionEqualToValue={(option, value) => option.idTipoDispositivo === value.idTipoDispositivo}
            getOptionLabel={(option: deviceOptionsProps | string) => {
                if (typeof option === 'string') {
                    return option;
                }

                return option.dsTipoDispositivo;
            }}
            renderInput={(params) => <CissTextField {...params} label={label} error={Boolean(error)} helperText={error} />}
            onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
            }}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
            renderOption={(props, option) => (
                <Box component="li" {...props} key={option.idTipoDispositivo}>
                    {option.idTipoDispositivo} - {option.dsTipoDispositivo}
                </Box>
            )}
        />
    );
}
